import { useEffect, useState } from 'react';

import { Page, PageHeader } from '../components/layout/AppShell';
import { Card, CardHeader, DetailList, DetailRow } from '../components/ui/Card';
import { Button, SegmentedControl } from '../components/ui/Button';
import { Field, Input, Select, Textarea } from '../components/ui/Field';
import { Badge } from '../components/ui/Badge';
import { Icon } from '../components/ui/Icon';
import { Tabs, useToast } from '../components/ui/Overlay';
import { AsyncBoundary, EmptyState, Skeleton, SkeletonRows } from '../components/ui/AsyncBoundary';
import { useResource, useMutation } from '../hooks/useResource';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../hooks/useTheme';
import { OrgSettingsModal } from '../components/domain/OrgModals';
import { api } from '../lib/api';
import { roleMeta, ROLES } from '../lib/domain';
import * as fmt from '../lib/format';

/**
 * Your own account.
 *
 * Three things people actually come here for: fix their name, change their
 * password, and ask for access they do not have. The last one is the reason
 * this page is reachable by every role rather than only by administrators.
 */
export function Settings() {
  const [tab, setTab] = useState('profile');
  const { can, user } = useAuth();

  // A platform administrator manages every organisation from the Platform
  // page, so the tab is for the person who runs exactly one: their own.
  const ownsAnOrg = can('org:settings') && user?.role !== 'SUPER_ADMIN';

  const tabs = [
    { value: 'profile', label: 'Profile', icon: 'users' },
    { value: 'security', label: 'Password', icon: 'shield' },
    { value: 'access', label: 'Access', icon: 'chip' },
    ...(ownsAnOrg ? [{ value: 'organisation', label: 'Organisation', icon: 'building' }] : []),
    { value: 'appearance', label: 'Appearance', icon: 'sun' },
  ];

  return (
    <Page className="max-w-3xl space-y-4">
      <PageHeader title="Settings" subtitle="Your account and how the app looks to you" />
      <Tabs tabs={tabs} value={tab} onChange={setTab} />

      {tab === 'profile' && <ProfilePanel />}
      {tab === 'security' && <PasswordPanel />}
      {tab === 'access' && <AccessPanel />}
      {tab === 'organisation' && ownsAnOrg && <OrganisationPanel orgId={user.orgId} />}
      {tab === 'appearance' && <AppearancePanel />}
    </Page>
  );
}

/* --------------------------------------------------------------- profile -- */

function ProfilePanel() {
  const toast = useToast();
  const { user, reload } = useAuth();
  const profile = useResource('/api/me');

  const [form, setForm] = useState({ fullName: '', phone: '' });

  useEffect(() => {
    if (profile.data?.profile) {
      setForm({
        fullName: profile.data.profile.full_name || '',
        phone: profile.data.profile.phone || '',
      });
    }
  }, [profile.data]);

  const save = useMutation(async () => {
    await api.patch('/api/me', { fullName: form.fullName.trim(), phone: form.phone.trim() });
  });

  const dirty =
    profile.data?.profile &&
    (form.fullName !== (profile.data.profile.full_name || '') ||
      form.phone !== (profile.data.profile.phone || ''));

  const submit = async () => {
    try {
      await save.run();
      toast.success('Profile updated.');
      profile.refetch();
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <AsyncBoundary state={profile} skeleton={<Skeleton className="h-64" />}>
      {(data) => (
        <div className="space-y-4">
          <Card>
            <CardHeader title="Your details" icon="users" />
            <div className="space-y-3 p-4">
              <Field label="Full name" hint="How you appear to everyone else in your organisation.">
                <Input
                  value={form.fullName}
                  onChange={(event) => setForm((c) => ({ ...c, fullName: event.target.value }))}
                />
              </Field>
              <Field label="Phone" hint="Optional. Used for alert notifications when those are enabled.">
                <Input
                  value={form.phone}
                  onChange={(event) => setForm((c) => ({ ...c, phone: event.target.value }))}
                  placeholder="+91 98765 43210"
                />
              </Field>
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  loading={save.pending}
                  onClick={submit}
                  disabled={!dirty || form.fullName.trim().length < 2}
                >
                  Save changes
                </Button>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Account"
              subtitle="Set by your administrator — ask on the Access tab to change your role"
              icon="shield"
            />
            <div className="px-4 py-2">
              <DetailList>
                <DetailRow label="Email" value={data.profile.email} />
                <DetailRow
                  label="Role"
                  value={
                    <Badge tone={roleMeta(data.profile.role).tone} size="sm" icon={null}>
                      {roleMeta(data.profile.role).label}
                    </Badge>
                  }
                />
                <DetailRow label="Organisation" value={data.profile.org_name || 'Platform'} />
                <DetailRow label="Member since" value={fmt.date(data.profile.created_at)} />
                <DetailRow label="Last signed in" value={fmt.relativeTime(data.profile.last_login_at)} />
              </DetailList>
            </div>
          </Card>
        </div>
      )}
    </AsyncBoundary>
  );
}

/* -------------------------------------------------------------- password -- */

function PasswordPanel() {
  const toast = useToast();
  const { signOut } = useAuth();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });

  const change = useMutation(async () => {
    await api.post('/api/auth/password', {
      currentPassword: form.currentPassword,
      newPassword: form.newPassword,
    });
  });

  const set = (key) => (event) => setForm((c) => ({ ...c, [key]: event.target.value }));

  const tooShort = form.newPassword.length > 0 && form.newPassword.length < 10;
  const mismatch = form.confirm.length > 0 && form.confirm !== form.newPassword;
  const same = form.newPassword.length > 0 && form.newPassword === form.currentPassword;

  const submit = async () => {
    try {
      await change.run();
      toast.success('Password changed. Sign in again on your other devices.');
      setForm({ currentPassword: '', newPassword: '', confirm: '' });
      // Changing a password ends every other session, this one included.
      setTimeout(signOut, 1200);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Change your password"
        subtitle="Every other session is signed out when you do"
        icon="shield"
      />
      <div className="space-y-3 p-4">
        <Field label="Current password" required>
          <Input type="password" autoComplete="current-password" value={form.currentPassword} onChange={set('currentPassword')} />
        </Field>

        <Field
          label="New password"
          required
          hint="At least 10 characters. A passphrase of three or four words is easier to remember and harder to guess."
          error={tooShort ? 'Use at least 10 characters.' : same ? 'Choose something different from your current password.' : null}
        >
          <Input type="password" autoComplete="new-password" value={form.newPassword} onChange={set('newPassword')} invalid={tooShort || same} />
        </Field>

        <Field label="Confirm new password" required error={mismatch ? 'These two do not match.' : null}>
          <Input type="password" autoComplete="new-password" value={form.confirm} onChange={set('confirm')} invalid={mismatch} />
        </Field>

        <div className="flex justify-end">
          <Button
            variant="primary"
            loading={change.pending}
            onClick={submit}
            disabled={
              !form.currentPassword || form.newPassword.length < 10 || mismatch || same || !form.confirm
            }
          >
            Change password
          </Button>
        </div>
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------- access -- */

/**
 * Ask for access.
 *
 * The counterpart to an administrator inviting someone. Asking grants nothing
 * on its own — a request is a message that someone with the right permission
 * approves or declines, and both halves are audited.
 */
function AccessPanel() {
  const toast = useToast();
  const { user } = useAuth();

  const [kind, setKind] = useState('VEHICLE');
  const [targetId, setTargetId] = useState('');
  const [role, setRole] = useState('MANAGER');
  const [reason, setReason] = useState('');

  const mine = useResource('/api/access-requests/mine', null, { pollMs: 60_000 });
  const vehicles = useResource(kind === 'VEHICLE' ? '/api/vehicles' : null, { limit: 500 });
  const fleets = useResource(kind === 'FLEET' ? '/api/fleets' : null);

  const submit = useMutation(async () => {
    await api.post('/api/access-requests', {
      kind,
      role: kind === 'ROLE' ? role : undefined,
      vehicleId: kind === 'VEHICLE' ? targetId : undefined,
      fleetId: kind === 'FLEET' ? targetId : undefined,
      reason: reason || undefined,
    });
  });

  const cancel = useMutation(async (id) => {
    await api.post(`/api/access-requests/${id}/cancel`, {});
  });

  const send = async () => {
    try {
      await submit.run();
      toast.success('Request sent. An administrator will see it in their queue.');
      setTargetId('');
      setReason('');
      mine.refetch();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // A person can only ask for a role above their own.
  const requestableRoles = {
    USER: ['MANAGER'],
    MANAGER: ['ORG_ADMIN'],
    ORG_ADMIN: [],
    SUPER_ADMIN: [],
  }[user?.role] || [];

  const kinds = [
    { value: 'VEHICLE', label: 'A vehicle' },
    { value: 'FLEET', label: 'A fleet' },
    ...(requestableRoles.length ? [{ value: 'ROLE', label: 'A role' }] : []),
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Request access"
          subtitle="Someone with the right permission decides — asking does not grant anything"
          icon="chip"
        />
        <div className="space-y-3 p-4">
          <Field label="What do you need access to?">
            <SegmentedControl value={kind} onChange={(next) => { setKind(next); setTargetId(''); }} options={kinds} size="md" />
          </Field>

          {kind === 'ROLE' && (
            <Field label="Role" hint="An administrator who can grant this role will review it.">
              <Select
                value={role}
                onChange={(event) => setRole(event.target.value)}
                options={requestableRoles.map((value) => ({ value, label: ROLES[value].label }))}
              />
            </Field>
          )}

          {kind === 'VEHICLE' && (
            <Field label="Vehicle" hint="Only vehicles in your organisation can be requested.">
              <Select
                value={targetId}
                onChange={(event) => setTargetId(event.target.value)}
                placeholder="Choose a vehicle"
                options={(vehicles.data?.vehicles || []).map((v) => ({
                  value: v.id,
                  label: `${v.reg_number}${v.fleet_name ? ` · ${v.fleet_name}` : ''}`,
                }))}
              />
            </Field>
          )}

          {kind === 'FLEET' && (
            <Field label="Fleet" hint="Managing a fleet means seeing every vehicle in it.">
              <Select
                value={targetId}
                onChange={(event) => setTargetId(event.target.value)}
                placeholder="Choose a fleet"
                options={(fleets.data?.fleets || []).map((f) => ({ value: f.id, label: f.name }))}
              />
            </Field>
          )}

          <Field label="Why do you need it?" hint="Optional, but it makes approval much faster.">
            <Textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Covering the Chennai route from Monday."
            />
          </Field>

          <div className="flex justify-end">
            <Button
              variant="accent"
              loading={submit.pending}
              onClick={send}
              disabled={kind !== 'ROLE' && !targetId}
            >
              Send request
            </Button>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader title="Your requests" icon="history" />
        <AsyncBoundary
          state={mine}
          skeleton={<Skeleton className="m-4 h-24" />}
          isEmpty={(data) => !data.requests.length}
          empty={<EmptyState icon="history" title="You have not asked for anything yet" />}
        >
          {(data) => (
            <ul className="divide-y divide-line">
              {data.requests.map((request) => (
                <li key={request.id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-[13.5px] text-ink">{describeRequest(request)}</p>
                    <p className="text-[12px] text-ink-3">
                      Asked {fmt.relativeTime(request.created_at)}
                      {request.decided_at
                        ? ` · decided ${fmt.relativeTime(request.decided_at)}${
                            request.decided_by_name ? ` by ${request.decided_by_name}` : ''
                          }`
                        : ''}
                      {request.decision_note ? ` · “${request.decision_note}”` : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge tone={requestTone(request.state)} size="sm" icon={null}>
                      {request.state.toLowerCase()}
                    </Badge>
                    {request.state === 'PENDING' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={cancel.pending}
                        onClick={async () => {
                          await cancel.run(request.id);
                          mine.refetch();
                        }}
                      >
                        Withdraw
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </AsyncBoundary>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------ appearance -- */

function AppearancePanel() {
  const { theme, setTheme, isSystem } = useTheme();

  return (
    <Card>
      <CardHeader title="Appearance" subtitle="Remembered in this browser" icon="sun" />
      <div className="space-y-4 p-4">
        <Field label="Theme">
          <SegmentedControl
            size="md"
            value={isSystem ? 'system' : theme}
            onChange={(value) => setTheme(value === 'system' ? null : value)}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
              { value: 'system', label: 'Match system' },
            ]}
          />
        </Field>
        <p className="flex items-start gap-2 text-[12.5px] text-ink-3">
          <Icon name="info" size="xs" className="mt-0.5" />
          Column choices are saved to your account instead, so the grid you set up on a desktop is
          the one you get on a tablet.
        </p>
      </div>
    </Card>
  );
}

/* ----------------------------------------------------------------- utils -- */

export function describeRequest(request) {
  if (request.kind === 'ROLE') return `Role: ${roleMeta(request.requested_role).label}`;
  if (request.kind === 'VEHICLE') return `Vehicle: ${request.reg_number || 'unknown'}`;
  if (request.kind === 'FLEET') return `Fleet: ${request.fleet_name || 'unknown'}`;
  return request.kind;
}

export function requestTone(state) {
  return { PENDING: 'warning', APPROVED: 'good', DENIED: 'critical', CANCELLED: 'neutral' }[state] || 'neutral';
}

/* ---------------------------------------------------------- organisation -- */

/**
 * The organisation an administrator runs.
 *
 * It is read-only detail plus one button, because there is exactly one thing
 * an organisation administrator can change about their tenant — its name — and
 * the rest is here so they can see what they are responsible for: how many
 * vehicles, devices and people sit inside the boundary.
 */
function OrganisationPanel({ orgId }) {
  const [editing, setEditing] = useState(false);
  const org = useResource(orgId ? `/api/orgs/${orgId}` : null);

  return (
    <AsyncBoundary state={org} skeleton={<SkeletonRows rows={4} />}>
      {(data) => {
        const o = data.organization;
        return (
          <>
            <Card>
              <CardHeader
                title={o.name}
                subtitle="Everything inside this boundary is invisible to every other organisation"
                icon="building"
                actions={
                  <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                    Edit
                  </Button>
                }
              />
              <div className="px-4 py-2">
                <DetailList>
                  <DetailRow label="Identifier" value={o.slug} mono />
                  <DetailRow label="Vehicles" value={o.vehicle_count ?? '—'} />
                  <DetailRow label="Devices" value={o.device_count ?? '—'} />
                  <DetailRow label="People" value={o.user_count ?? '—'} />
                  <DetailRow label="Status" value={String(o.status).toLowerCase()} />
                  <DetailRow label="Created" value={fmt.date(o.created_at)} />
                </DetailList>
              </div>
            </Card>

            <OrgSettingsModal
              open={editing}
              organization={o}
              onClose={() => setEditing(false)}
              onSaved={() => {
                setEditing(false);
                org.refetch();
              }}
            />
          </>
        );
      }}
    </AsyncBoundary>
  );
}
